import { default as React } from 'react';
import Link from '@mui/material/Link';

/**
 * Regex that matches HTTP(S) URLs in plain text. Captures common URL
 * characters including path segments, query strings, and fragments.
 */
const URL_REGEX = /https?:\/\/[^\s<>"'`}\]]+/gi;

/**
 * Trims trailing punctuation (. , ; : ! ?) from a URL match so that
 * sentence-ending punctuation is not included in the href.
 */
function trimTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?]+$/, '');
}

/**
 * Splits a plain-text string on URL boundaries and returns an array of
 * React nodes where each URL is wrapped in an MUI `<Link>` that opens in
 * a new tab.
 *
 * Non-URL text is returned as plain strings. When the input contains no
 * URLs the original string is returned inside a single-element array.
 */
export function linkifyText(text: string): React.ReactNode[] {
  const matches = Array.from(text.matchAll(URL_REGEX));
  if (matches.length === 0) {
    return [text];
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex));
    }
    const rawUrl = match[0];
    const url = trimTrailingPunctuation(rawUrl);
    parts.push(
      <Link
        key={matchIndex}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ wordBreak: 'break-all' }}
      >
        {url}
      </Link>,
    );
    // Append any trimmed punctuation as plain text
    if (url.length < rawUrl.length) {
      parts.push(rawUrl.slice(url.length));
    }
    lastIndex = matchIndex + rawUrl.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

/**
 * Scans a DOM subtree for text nodes that contain URLs and wraps each URL
 * in an `<a>` element. Intended for post-processing Backstage's built-in
 * log viewer output where we cannot control the React rendering.
 *
 * Only processes `<pre>` blocks and elements with `LogViewer` or `logLine`
 * in their class name to avoid modifying interactive elements. Skips nodes
 * that have already been linkified.
 */
export function linkifyDomElement(root: Element): void {
  const selector = 'pre, [class*="LogViewer"], [class*="logLine"]';
  const allTargets = [
    ...(root.matches(selector) ? [root] : []),
    ...Array.from(root.querySelectorAll(selector)),
  ];
  // Only collect the deepest matching elements (those with no matching ancestor already in the set)
  const targets = allTargets.filter(
    el => !allTargets.some(t => t !== el && t.contains(el)),
  );

  targets.forEach(target => {
    const walker = document.createTreeWalker(
      target,
      NodeFilter.SHOW_TEXT,
      null,
    );

    const textNodes: Text[] = [];
    let node = walker.nextNode();
    for (; node !== null; node = walker.nextNode()) {
      const parent = node.parentElement;
      if (parent?.hasAttribute?.('data-linkified')) {
        continue;
      }
      if (parent?.tagName === 'A') {
        continue;
      }
      textNodes.push(node as Text);
    }

    textNodes.forEach(textNode => {
      const content = textNode.textContent ?? '';
      const matches = Array.from(content.matchAll(URL_REGEX));
      if (matches.length === 0) {
        return;
      }

      const fragment = document.createDocumentFragment();
      let lastIdx = 0;

      for (const m of matches) {
        const mIndex = m.index ?? 0;
        if (mIndex > lastIdx) {
          fragment.appendChild(
            document.createTextNode(content.slice(lastIdx, mIndex)),
          );
        }
        const rawUrl = m[0];
        const url = trimTrailingPunctuation(rawUrl);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.textContent = url;
        anchor.style.color = 'inherit';
        anchor.style.textDecoration = 'underline';
        anchor.style.wordBreak = 'break-all';
        anchor.setAttribute('data-linkified', 'true');
        fragment.appendChild(anchor);
        // Append any trimmed punctuation as plain text
        if (url.length < rawUrl.length) {
          fragment.appendChild(
            document.createTextNode(rawUrl.slice(url.length)),
          );
        }
        lastIdx = mIndex + rawUrl.length;
      }

      if (lastIdx < content.length) {
        fragment.appendChild(document.createTextNode(content.slice(lastIdx)));
      }

      textNode.parentNode?.replaceChild(fragment, textNode);
    });
  });
}
